import React, { useEffect, useState, useMemo } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from 'lucide-react';
import {
  getSymptomMappings,
  getNormalizedSymptoms,
  getSpecialities,
  createSymptomMapping,
  updateSymptomMapping,
  deleteSymptomMapping,
  type SymptomMappingRow,
  type SpecialityDropdownItem,
} from '../../services/MasterAdmin/symptomMappingAdminService';

const SymptomSpecialityMappingSection: React.FC = () => {
  // Data lists from MySQL
  const [specialities, setSpecialities] = useState<SpecialityDropdownItem[]>([]);
  const [normalizedSymptoms, setNormalizedSymptoms] = useState<string[]>([]);
  const [mappings, setMappings] = useState<SymptomMappingRow[]>([]);
  
  // UI Statuses
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Search/Selection state
  const [selectedSpecialityId, setSelectedSpecialityId] = useState('');
  const [selectedSymptom, setSelectedSymptom] = useState('');
  const [inspectKeywords, setInspectKeywords] = useState<string[]>([]);

  // Text inputs & Autocomplete dropdown values
  const [specialitySearch, setSpecialitySearch] = useState('');
  const [symptomSearch, setSymptomSearch] = useState('');
  const [keywordInput, setKeywordInput] = useState('');

  // Dropdown open states
  const [symptomDropdownOpen, setSymptomDropdownOpen] = useState(false);
  const [specialityDropdownOpen, setSpecialityDropdownOpen] = useState(false);

  // Fetch initial data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [specRes, normRes, mapRes] = await Promise.all([
        getSpecialities(),
        getNormalizedSymptoms(),
        getSymptomMappings({ limit: 1000 }), // Load all mapping rows
      ]);

      setSpecialities(specRes.data || []);
      setNormalizedSymptoms(normRes.data || []);
      setMappings(mapRes.data?.mappings || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load mapping data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filter lists based on user search inputs
  const filteredSymptomDropdown = useMemo(() => {
    const term = symptomSearch.toLowerCase().trim();
    if (!term) return normalizedSymptoms;
    return normalizedSymptoms.filter((s) => s.toLowerCase().includes(term));
  }, [symptomSearch, normalizedSymptoms]);

  const filteredSpecialityDropdown = useMemo(() => {
    const term = specialitySearch.toLowerCase().trim();
    if (!term) return specialities;
    return specialities.filter((s) => s.name.toLowerCase().includes(term));
  }, [specialitySearch, specialities]);

  // Determine if there is an existing mapping loaded for the current specialization or symptom
  const loadedMapping = useMemo(() => {
    if (selectedSpecialityId) {
      return mappings.find((m) => m.speciality_id === selectedSpecialityId);
    }
    if (selectedSymptom) {
      return mappings.find((m) => m.normalized_symptom.toLowerCase() === selectedSymptom.toLowerCase());
    }
    return null;
  }, [selectedSpecialityId, selectedSymptom, mappings]);

  // Check if loaded mapping's keywords have changed
  const hasKeywordsChanged = useMemo(() => {
    if (!loadedMapping) return false;
    const orig = [...loadedMapping.keywords].sort();
    const current = [...inspectKeywords].sort();
    return JSON.stringify(orig) !== JSON.stringify(current);
  }, [loadedMapping, inspectKeywords]);

  // Check if user is typing an unknown symptom name that does not exist in MySQL
  const symptomExistsInDb = useMemo(() => {
    return normalizedSymptoms.some(
      (s) => s.toLowerCase() === selectedSymptom.toLowerCase()
    );
  }, [selectedSymptom, normalizedSymptoms]);

  const isUnknownSymptom = useMemo(() => {
    return selectedSymptom && !symptomExistsInDb && !selectedSpecialityId;
  }, [selectedSymptom, symptomExistsInDb, selectedSpecialityId]);

  // Check if typed symptom key is already used by another specialization
  const duplicateSymptomMapping = useMemo(() => {
    if (!selectedSymptom || loadedMapping) return null;
    return mappings.find(
      (m) => m.normalized_symptom.toLowerCase() === selectedSymptom.toLowerCase()
    );
  }, [selectedSymptom, loadedMapping, mappings]);

  // Handle Speciality Autocomplete selection
  const handleSelectSpeciality = (specId: string, specName: string) => {
    setSelectedSpecialityId(specId);
    setSpecialitySearch(specName);
    setSpecialityDropdownOpen(false);

    // Auto-fill mapped normalized symptom and keywords if mapping exists
    const matchedMapping = mappings.find((m) => m.speciality_id === specId);
    if (matchedMapping) {
      setSelectedSymptom(matchedMapping.normalized_symptom);
      setSymptomSearch(matchedMapping.normalized_symptom);
      setInspectKeywords(matchedMapping.keywords);
    } else {
      setSelectedSymptom('');
      setSymptomSearch('');
      setInspectKeywords([]);
    }
    setError(null);
  };

  // Handle Symptom Autocomplete selection
  const handleSelectSymptom = (symptomName: string) => {
    setSelectedSymptom(symptomName);
    setSymptomSearch(symptomName);
    setSymptomDropdownOpen(false);

    // Auto-fill mapped specialization and keywords if mapping exists
    const matchedMapping = mappings.find(
      (m) => m.normalized_symptom.toLowerCase() === symptomName.toLowerCase()
    );
    if (matchedMapping) {
      setSelectedSpecialityId(matchedMapping.speciality_id);
      const specName =
        specialities.find((s) => s.id === matchedMapping.speciality_id)?.name || '';
      setSpecialitySearch(specName);
      setInspectKeywords(matchedMapping.keywords);
    } else {
      setSelectedSpecialityId('');
      setSpecialitySearch('');
      setInspectKeywords([]);
    }
    setError(null);
  };

  // Keywords Tags insertion
  const handleAddKeywordTag = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanKw = keywordInput.trim().toLowerCase();
    if (!cleanKw) return;

    if (inspectKeywords.includes(cleanKw)) {
      setKeywordInput('');
      return;
    }
    if (inspectKeywords.length >= 10) {
      setError('You can add up to 10 keywords maximum per mapping.');
      return;
    }

    setInspectKeywords((prev) => [...prev, cleanKw]);
    setKeywordInput('');
    setError(null);
  };

  const handleRemoveKeywordTag = (index: number) => {
    setInspectKeywords((prev) => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // Clear panel selection
  const handleClearSelection = () => {
    setSelectedSpecialityId('');
    setSelectedSymptom('');
    setSpecialitySearch('');
    setSymptomSearch('');
    setKeywordInput('');
    setInspectKeywords([]);
    setError(null);
  };

  // Save new mapping to MySQL
  const handleSaveNewMapping = async () => {
    if (!selectedSpecialityId || !selectedSymptom || inspectKeywords.length === 0) {
      setError('Please fill in Specialization, Normalized Symptom key, and add at least 1 keyword.');
      return;
    }
    const cleanSymptomName = selectedSymptom.trim().toLowerCase();
    if (/[^a-z0-9_]/.test(cleanSymptomName)) {
      setError('Normalized symptom must only contain lowercase letters, numbers, or underscores.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        normalized_symptom: cleanSymptomName,
        speciality_id: selectedSpecialityId,
        keywords: inspectKeywords,
      };

      const res = await createSymptomMapping(payload);
      if (res.success) {
        setNotice('New speciality mapping successfully saved.');
        handleClearSelection();
        await loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save mapping.');
    } finally {
      setSaving(false);
    }
  };

  // Update existing mapping keywords in MySQL
  const handleUpdateExistingMapping = async () => {
    if (!loadedMapping) return;
    if (inspectKeywords.length === 0) {
      setError('Please add at least 1 keyword.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        normalized_symptom: loadedMapping.normalized_symptom,
        speciality_id: loadedMapping.speciality_id,
        keywords: inspectKeywords,
      };

      const res = await updateSymptomMapping(loadedMapping.id, payload);
      if (res.success) {
        setNotice('Speciality mapping keywords updated successfully.');
        await loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update mapping.');
    } finally {
      setSaving(false);
    }
  };

  // Delete mapping from MySQL
  const handleDeleteExistingMapping = async () => {
    if (!loadedMapping) return;
    if (!window.confirm(`Are you sure you want to delete the mapping for ${loadedMapping.normalized_symptom}?`)) {
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await deleteSymptomMapping(loadedMapping.id);
      if (res.success) {
        setNotice('Mapping deleted successfully.');
        handleClearSelection();
        await loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete mapping.');
    } finally {
      setSaving(false);
    }
  };

  // Auto-close dropdowns on document click
  useEffect(() => {
    const handleDocClick = () => {
      setSymptomDropdownOpen(false);
      setSpecialityDropdownOpen(false);
    };
    document.addEventListener('click', handleDocClick);
    return () => document.removeEventListener('click', handleDocClick);
  }, []);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-100 pb-3 dark:border-gray-700">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white">
          Speciality Mapping
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Map doctor specialities to normalized symptoms and patient search keywords.
        </p>
      </div>

      {/* Alerts */}
      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
          <button type="button" className="ml-auto" onClick={() => setNotice(null)} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button type="button" className="ml-auto" onClick={() => setError(null)} aria-label="Dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Unified Mapping Card Panel */}
      <div className="p-6 bg-white rounded-xl border border-gray-200 dark:bg-gray-800 dark:border-gray-700 space-y-5">
        {/* Warnings */}
        {loadedMapping && (
          <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-bold text-sm">Mapping already exists.</span>
              <p className="mt-1 font-normal text-xs leading-normal">
                Specialization <strong className="font-semibold">{specialities.find(s => s.id === loadedMapping.speciality_id)?.name || 'Unknown'}</strong> is already mapped to normalized symptom key <strong className="font-semibold">{loadedMapping.normalized_symptom}</strong>.
              </p>
            </div>
          </div>
        )}

        {isUnknownSymptom && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-bold text-sm">Symptom does not exist.</span>
              <p className="mt-1 font-normal text-xs leading-normal">
                The normalized symptom key <strong className="font-semibold">&quot;{selectedSymptom}&quot;</strong> is not mapped to any doctor specialization in the database.
              </p>
            </div>
          </div>
        )}

        {!loadedMapping && duplicateSymptomMapping && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-800 dark:border-red-800 dark:bg-red-950/20 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <span className="font-bold text-sm">Symptom key already in use.</span>
              <p className="mt-1 font-normal text-xs leading-normal">
                The symptom key <strong className="font-semibold">&quot;{selectedSymptom}&quot;</strong> is already mapped to specialization <strong className="font-semibold">{specialities.find(s => s.id === duplicateSymptomMapping.speciality_id)?.name || 'Unknown'}</strong>. Specialization mapping must be unique.
              </p>
            </div>
          </div>
        )}

        {/* Form Inputs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Field 1: Specialization Dropdown */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
              Specialization *
            </label>
            <input
              type="text"
              placeholder="Search or select specialization..."
              value={specialitySearch}
              onFocus={() => {
                setSpecialityDropdownOpen(true);
                setSymptomDropdownOpen(false);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSpecialitySearch(val);
                setSpecialityDropdownOpen(true);
                if (!val) {
                  setSelectedSpecialityId('');
                  if (!selectedSymptom) {
                    setInspectKeywords([]);
                  }
                }
              }}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm bg-white dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            {specialityDropdownOpen && (
              <div className="absolute left-0 right-0 top-[74px] z-30 max-h-[180px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                {filteredSpecialityDropdown.length === 0 ? (
                  <div className="px-3 py-2.5 text-xs text-gray-400 italic">No matches</div>
                ) : (
                  filteredSpecialityDropdown.map((spec) => (
                    <button
                      key={spec.id}
                      type="button"
                      onClick={() => handleSelectSpeciality(spec.id, spec.name)}
                      className="w-full text-left px-3.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 text-xs"
                    >
                      {spec.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Field 2: Normalized Symptom Input / Dropdown */}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
              Normalized Symptom (Unique Key) *
            </label>
            <input
              type="text"
              placeholder="Search or type symptom key..."
              value={symptomSearch}
              onFocus={() => {
                setSymptomDropdownOpen(true);
                setSpecialityDropdownOpen(false);
              }}
              onChange={(e) => {
                const val = e.target.value;
                setSymptomSearch(val);
                setSelectedSymptom(val);
                setSymptomDropdownOpen(true);
                if (!val) {
                  setSelectedSymptom('');
                  if (!selectedSpecialityId) {
                    setInspectKeywords([]);
                  }
                }
              }}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm bg-white dark:border-gray-600 dark:bg-gray-900 dark:text-white disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-500"
            />
            {symptomDropdownOpen && filteredSymptomDropdown.length > 0 && (
              <div className="absolute left-0 right-0 top-[74px] z-30 max-h-[180px] overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                {filteredSymptomDropdown.map((symptom) => (
                  <button
                    key={symptom}
                    type="button"
                    onClick={() => handleSelectSymptom(symptom)}
                    className="w-full text-left px-3.5 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-200 text-xs"
                  >
                    {symptom}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Tag Input for Keywords */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300">
            Search Keywords (Synonyms) — add up to 10
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Type keyword and press Enter or click Add"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddKeywordTag();
                }
              }}
              className="flex-1 rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm bg-white dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => handleAddKeywordTag()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>

          {/* Keywords tags display cloud */}
          {inspectKeywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 p-3 bg-gray-50 rounded-lg border border-gray-200 dark:bg-gray-900/40 dark:border-gray-700">
              {inspectKeywords.map((kw, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900/30 font-mono"
                >
                  {kw}
                  <button
                    type="button"
                    onClick={() => handleRemoveKeywordTag(i)}
                    className="text-emerald-600 hover:text-emerald-900 dark:text-emerald-400 dark:hover:text-white"
                    aria-label={`Remove keyword ${kw}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Buttons / Actions Row */}
        <div className="flex flex-wrap justify-end gap-2.5 pt-3.5 border-t border-gray-100 dark:border-gray-700/60">
          {(selectedSpecialityId || selectedSymptom || inspectKeywords.length > 0) && (
            <button
              type="button"
              onClick={handleClearSelection}
              className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Clear selection
            </button>
          )}

          {loadedMapping ? (
            <>
              {hasKeywordsChanged && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={handleUpdateExistingMapping}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  Update Mapping
                </button>
              )}
              <button
                type="button"
                disabled={saving}
                onClick={handleDeleteExistingMapping}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                <Trash2 className="h-4 w-4" />
                Delete Mapping
              </button>
            </>
          ) : (
            selectedSpecialityId && selectedSymptom && !duplicateSymptomMapping && (
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveNewMapping}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                <Plus className="h-4 w-4" />
                Save Mapping
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SymptomSpecialityMappingSection;
